import React, { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";
import { fetchUnifiedSearch } from "../PhotoManager/API/api";
import { getEncodedEntityPath } from "../PhotoManager/components/FolderFeatures/EncryptionOfFolder";
import ContentCard from "./components/ContentCard";
import { LibraryEntity } from "./types";
import SelfieSearchDropdown from "./filters/SelfieSearchDropdown";

const eventIcon = require("../PhotoManager/styles/images/Event.svg");
const selfieSearchIcon = require("./style/selfie-search.svg");
const tagIcon = require("./style/tag-icon.svg");
const groupIcon = require("./style/group-icon.svg");
const peopleIcon = require("./style/people-icon.svg");

interface UnifiedSearchResult {
  data: {
    photos: Array<{
      id: number;
      thumbnail_url: string | null;
      album_id: number;
      album_name: string;
      event_id: number;
      event_name: string;
      created_at: string;
    }>;
    categories: {
      tags: Array<{
        id: number;
        name: string;
        photoCount: number;
        type: string;
      }>;
      libraryEntities: Array<{
        id: number;
        name: string;
        type: "event" | "album" | "folder";
        description: string;
        createdAt: string;
        path: string[];
      }>;
      groups: Array<{
        id: number;
        name: string;
        photoCount: number;
        type: string;
      }>;
      people: Array<{
        id: number;
        name: string;
        photoCount: number;
        thumbnailImage: string | null;
        type: string;
      }>;
    };
  };
  meta: {
    page: number;
    totalCount: number;
    hasMore: boolean;
    countsByType: {
      photos: number;
      tags: number;
      libraryEntities: number;
      groups: number;
      people: number;
    };
  };
}

interface EnhancedSearchBarProps {
  placeholder: string;
  isMobile?: boolean;
  enabledForUser?: boolean;
  onResultsChange?: (results: UnifiedSearchResult | null) => void;
  selfieSearchS3Bucket?: string;
}

const EnhancedSearchBar: React.FC<EnhancedSearchBarProps> = ({
  placeholder,
  isMobile = false,
  enabledForUser = false,
  onResultsChange,
  selfieSearchS3Bucket = "geosnapshot-selfie-search"
}) => {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] =
    useState<UnifiedSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showSelfiePopup, setShowSelfiePopup] = useState(false);
  const [selfieFilter, setSelfieFilter] = useState<{ imageUrl: string | null }>(
    () => {
      const urlParams = new URLSearchParams(window.location.search);
      const selfieImage = urlParams.get("selfie_image");
      return { imageUrl: selfieImage };
    }
  );

  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  const fetchDebouncedResults = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchResults(null);
        setIsLoading(false);
        setShowResults(false);
        if (onResultsChange) onResultsChange(null);
        return;
      }

      if (!isMountedRef.current) return;

      setIsLoading(true);
      try {
        const results = await fetchUnifiedSearch(searchTerm, {
          page: 1,
          limit: 20
        });
        if (!isMountedRef.current) return;

        setSearchResults(results);
        setShowResults(true);
        if (onResultsChange) onResultsChange(results);
      } catch (error) {
        console.error("Search error:", error);
        if (!isMountedRef.current) return;

        setSearchResults(null);
        if (onResultsChange) onResultsChange(null);
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    }, 500),
    [onResultsChange]
  );

  useEffect(() => {
    fetchDebouncedResults(query);
    return () => fetchDebouncedResults.cancel();
  }, [query, fetchDebouncedResults]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleCloseClick = () => {
      setQuery("");
      setSearchResults(null);
      setShowResults(false);
      if (onResultsChange) onResultsChange(null);

      if (typeof $ !== "undefined") {
        $("#mobile-nav-with-only-search")
          .hide()
          .removeClass("only-search-show");
        $("#mobile-nav-with-icons").show();
      }
    };

    if (typeof $ !== "undefined") {
      $("#close-search-only-nav").on("click", handleCloseClick);
    }

    return () => {
      if (typeof $ !== "undefined") {
        $("#close-search-only-nav").off("click", handleCloseClick);
      }
    };
  }, [onResultsChange]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!enabledForUser) {
    return null;
  }
  const generateEntityURL = (entity: any) => {
    const { type, id } = entity;

    switch (type) {
      case "event": {
        const galleryPath = `g:${id}`;
        const encodedGalleryPath = getEncodedEntityPath(galleryPath);
        return `/library?p=${encodedGalleryPath}`;
      }

      case "album": {
        const albumPath = entity.parentId
          ? `g:${entity.parentId}/a:${id}`
          : `a:${id}`;
        const encodedAlbumPath = getEncodedEntityPath(albumPath);
        return `/library?p=${encodedAlbumPath}`;
      }

      case "folder": {
        const folderPath = `f:${id}`;
        const encodedFolderPath = getEncodedEntityPath(folderPath);
        return `/library?p=${encodedFolderPath}`;
      }

      default:
        return `/library`;
    }
  };

  const handleEntityClick = (entity: LibraryEntity) => {
    const url = generateEntityURL(entity);

    const windowUrl = window.location.href;
    const searchString = "/library";
    if (windowUrl.includes(searchString)) {
      window.history.pushState({}, "", url);
    } else {
      window.location.assign(url);
    }

    setQuery("");
    setSearchResults(null);
    setShowResults(false);

    window.dispatchEvent(new Event("popstate"));
  };

  const handleInputFocus = () => {
    if (searchResults && query.trim()) {
      setShowResults(true);
    }
  };

  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;

    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escapedTerm})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={index} className="enhanced-search-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleSelfieClick = () => {
    setShowSelfiePopup(true);
  };

  const handleSelfieChange = (updated: { imageUrl: string | null }) => {
    if (updated.imageUrl) {
      // Close popup and redirect immediately for better UX
      setShowSelfiePopup(false);

      // Preserve the search query if we're currently on a search results page
      const urlParams = new URLSearchParams(window.location.search);
      const currentQuery = urlParams.get("q");
      const selfieParam = `selfie_image=${encodeURIComponent(
        updated.imageUrl
      )}`;
      const queryParam = currentQuery
        ? `q=${encodeURIComponent(currentQuery)}&`
        : "";

      window.location.href = `/search-results?${queryParam}${selfieParam}`;
    } else {
      setShowSelfiePopup(false);
      window.location.href = "/library";
    }
    setSelfieFilter(updated);
  };

  const handleCloseSelfiePopup = () => {
    setShowSelfiePopup(false);
  };

  const hasResults =
    searchResults &&
    searchResults.data &&
    searchResults.data.categories &&
    ((searchResults.data.categories.tags?.length || 0) > 0 ||
      (searchResults.data.categories.libraryEntities?.length || 0) > 0 ||
      (searchResults.data.categories.groups?.length || 0) > 0 ||
      (searchResults.data.categories.people?.length || 0) > 0);

  const renderSearchInputWrapper = () => (
    <div className="enhanced-search-input-wrapper">
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className="enhanced-search-input"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={handleInputFocus}
        {...(isMobile && { id: "event-search-mobile" })}
      />
      <button
        className="enhanced-search-selfie-btn"
        type="button"
        aria-label="Selfie search"
        onClick={handleSelfieClick}
      >
        <img
          src={selfieSearchIcon}
          alt="Selfie search"
          className="enhanced-search-selfie-icon"
        />
        <span className="enhanced-search-selfie-text">Selfie search</span>
      </button>
    </div>
  );

  return (
    <div className="top-bar-search">
      <div
        className={`enhanced-search-container ${
          (showResults && hasResults) ||
          (showResults && !hasResults && !isLoading) ||
          (isLoading && query.trim())
            ? isMobile
              ? "enhanced-search-border-bottom"
              : "enhanced-search-border-full"
            : ""
        }`}
        ref={searchContainerRef}
      >
        {isMobile ? (
          <div className="search-highlighter">
            {renderSearchInputWrapper()}
            <div id="close-search-only-nav" className="search-close">
              <img
                alt="close-search"
                src="/images/website/home/close.svg"
                className="close-search-nav-icon"
              />
            </div>
            {isLoading && (
              <div className="v3-spinner-mob">
                <span className="fa fa-spinner fa-spin"></span>
              </div>
            )}
          </div>
        ) : (
          renderSearchInputWrapper()
        )}

        {(isLoading && query.trim()) || (showResults && query.trim()) ? (
          <div className="enhanced-search-dropdown">
            {isLoading && (
              <div className="enhanced-search-loading-dropdown">
                <div className="enhanced-search-searching-text">
                  Searching...
                </div>
              </div>
            )}

            {showResults && !hasResults && !isLoading && (
              <ul className="enhanced-search-results">
                <p className="enhanced-search-no-results">
                  No search results found for "{query}"
                </p>
              </ul>
            )}

            {showResults && hasResults && !isLoading && (
              <ul className="enhanced-search-results">
                {searchResults?.data?.categories?.tags &&
                  searchResults.data.categories.tags.length > 0 && (
                    <>
                      <li className="enhanced-search-section-header">
                        <img
                          src={tagIcon}
                          alt="Tags"
                          className="enhanced-search-section-icon"
                        />
                        <span>Tags</span>
                      </li>
                      <div className="unified-search-tags-grid">
                        {searchResults.data.categories.tags
                          .slice(0, 3)
                          .map(tag => (
                            <button
                              key={`tag-${tag.id}`}
                              className="unified-search-tag-card"
                              onClick={() => {
                                window.location.href = `/search-results?entity_type=tag&entity_id=${
                                  tag.id
                                }&entity_name=${encodeURIComponent(tag.name)}`;
                              }}
                            >
                              {tag.name}
                            </button>
                          ))}
                      </div>
                      {searchResults.data.categories.tags.length > 3 && (
                        <li className="enhanced-search-see-all-item">
                          <button
                            className="enhanced-search-see-all-btn"
                            onClick={() => {
                              window.location.href = `/search-results?q=${encodeURIComponent(
                                query
                              )}&tab=tags`;
                            }}
                          >
                            See all
                          </button>
                        </li>
                      )}
                      <hr />
                    </>
                  )}

                {searchResults?.data?.categories?.libraryEntities &&
                  searchResults.data.categories.libraryEntities.length > 0 && (
                    <>
                      <li className="enhanced-search-section-header">
                        <img
                          src={eventIcon}
                          alt="Content"
                          className="enhanced-search-section-icon"
                        />
                        <span>Content</span>
                      </li>
                      <div className="es-result-items enhanced-search-content-wrapper">
                        {searchResults.data.categories.libraryEntities
                          .slice(0, 2)
                          .map(entity => (
                            <ContentCard
                              key={`${entity.type}-${entity.id}`}
                              entity={entity}
                              query={query}
                              onClick={handleEntityClick}
                              highlightSearchTerm={highlightSearchTerm}
                              showTypeLabel={true}
                            />
                          ))}
                      </div>
                      {searchResults.data.categories.libraryEntities.length >
                        2 && (
                        <li className="enhanced-search-see-all-item">
                          <button
                            className="enhanced-search-see-all-btn"
                            onClick={() => {
                              window.location.href = `/search-results?q=${encodeURIComponent(
                                query
                              )}&tab=content`;
                            }}
                          >
                            See all
                          </button>
                        </li>
                      )}
                      <hr />
                    </>
                  )}

                {searchResults?.data?.categories?.groups &&
                  searchResults.data.categories.groups.length > 0 && (
                    <>
                      <li className="enhanced-search-section-header">
                        <img
                          src={groupIcon}
                          alt="Groups"
                          className="enhanced-search-section-icon"
                        />
                        <span>Groups</span>
                      </li>
                      <div className="unified-search-tags-grid">
                        {searchResults.data.categories.groups
                          .slice(0, 6)
                          .map(group => (
                            <div
                              key={`group-${group.id}`}
                              className="unified-search-tag-card"
                            >
                              {group.name}
                            </div>
                          ))}
                      </div>
                      {searchResults.data.categories.groups.length > 6 && (
                        <li className="enhanced-search-see-all-item">
                          <button
                            className="enhanced-search-see-all-btn"
                            onClick={() => {
                              window.location.href = `/search-results?q=${encodeURIComponent(
                                query
                              )}&tab=groups`;
                            }}
                          >
                            See all
                          </button>
                        </li>
                      )}
                      <hr />
                    </>
                  )}

                {searchResults?.data?.categories?.people &&
                  searchResults.data.categories.people.length > 0 && (
                    <>
                      <li className="enhanced-search-section-header">
                        <img
                          src={peopleIcon}
                          alt="People"
                          className="enhanced-search-section-icon"
                        />
                        <span>People</span>
                      </li>
                      {searchResults.data.categories.people
                        .slice(0, 2)
                        .map(person => (
                          <li
                            key={`person-${person.id}`}
                            className="enhanced-search-result-item"
                          >
                            <button
                              className="enhanced-search-person-item"
                              onClick={() => {
                                window.location.href = `/search-results?entity_type=person&entity_id=${
                                  person.id
                                }&entity_name=${encodeURIComponent(
                                  person.name
                                )}`;
                              }}
                            >
                              <div className="enhanced-search-person-photo">
                                {person.thumbnailImage ? (
                                  <img
                                    src={person.thumbnailImage}
                                    alt={person.name}
                                    className="enhanced-search-person-thumbnail"
                                  />
                                ) : (
                                  <div className="enhanced-search-person-placeholder">
                                    {person.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div className="enhanced-search-person-details">
                                <div className="enhanced-search-person-name">
                                  {highlightSearchTerm(person.name, query)}
                                </div>
                                <div className="enhanced-search-person-count">
                                  {person.photoCount}{" "}
                                  {person.photoCount === 1 ? "photo" : "photos"}
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      {searchResults.data.categories.people.length > 2 && (
                        <li className="enhanced-search-see-all-item">
                          <button
                            className="enhanced-search-see-all-btn"
                            onClick={() => {
                              window.location.href = `/search-results?q=${encodeURIComponent(
                                query
                              )}&tab=people`;
                            }}
                          >
                            See all
                          </button>
                        </li>
                      )}
                      <hr />
                    </>
                  )}
              </ul>
            )}
            {showResults && hasResults && !isLoading && (
              <div className="enhanced-search-all-results">
                <button
                  className="enhanced-search-all-results-btn"
                  onClick={() => {
                    window.location.href = `/search-results?q=${encodeURIComponent(
                      query
                    )}`;
                  }}
                >
                  See all results
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {showSelfiePopup && (
        <SelfieSearchDropdown
          value={selfieFilter}
          onChange={handleSelfieChange}
          onClose={handleCloseSelfiePopup}
          selfieSearchS3Bucket={selfieSearchS3Bucket}
        />
      )}
    </div>
  );
};

export default EnhancedSearchBar;
